/* ═══════════════════════════════════════════════════════════════
   Pending Invitations block — ported verbatim from the old
   MembersTab.tsx (envelope avatar, name-or-email + email, role
   pill, "Pending" italic, X = cancel invitation).
   ═══════════════════════════════════════════════════════════════ */

import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import type { Role, Invitation } from '../../../shared/types';

export interface PendingInvitationsProps {
  invitations: Invitation[];
  roles: Role[];
  onCancel: (id: string) => void;
}

export function PendingInvitations({ invitations, roles, onCancel }: PendingInvitationsProps) {
  if (invitations.length === 0) return null;
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 700,
          color: 'var(--tm)',
          marginBottom: 'var(--space-2)',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        Pending Invitations ({invitations.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {invitations.map((inv) => (
          <div
            key={inv.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: `${rem(8)} ${rem(14)}`,
              background: 'var(--sf)',
              border: '1px dashed var(--bd)',
              borderRadius: rem(8),
            }}
          >
            <div
              style={{
                width: rem(32),
                height: rem(32),
                borderRadius: '50%',
                background: 'var(--sa)',
                border: '1px solid var(--bd)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                width={rem(14)}
                height={rem(14)}
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--tm)"
                strokeWidth="2"
              >
                <path d="M4 4h16v16H4z" />
                <path d="M22 6l-10 7L2 6" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                {inv.name || inv.email}
              </div>
              {inv.name && (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  {inv.email}
                </div>
              )}
            </div>
            {inv.roleId &&
              (() => {
                const r = roles.find((r) => r.id === inv.roleId);
                return r ? (
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      padding: `${rem(2)} ${rem(8)}`,
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--sa)',
                      border: '1px solid var(--bd)',
                      fontWeight: 500,
                    }}
                  >
                    {r.name}
                  </span>
                ) : null;
              })()}
            <span
              style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', fontStyle: 'italic' }}
            >
              Pending
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-danger"
              onClick={() => onCancel(inv.id)}
              title="Cancel invitation"
              aria-label="Cancel invitation"
            >
              <Icon icon={X} em={1.0769} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
